import React, { useState, useEffect, useRef, useCallback } from "react";
import { notesService } from "../../services/notesService";
import { sessionService } from "../../services/sessionService";
import { NotesUpdatedData, NotesLockUpdatedData } from "../../types/websocket";
import { useWebSocketContext } from "../../contexts/WebSocketContext";
import { getTheme } from "../../config/sessionThemes";
import { MarkdownNotes } from "./MarkdownNotes";
import { CampaignAssetService } from "../../services/campaignAssetService";
import { CharacterService } from "../../services/characterService";
import type { CampaignAsset, CharacterListItem } from "../../types/types";

type SaveState = "idle" | "saving" | "saved" | "error";

interface NotesSectionProps {
  label: string;
  value: string;
  readOnly?: boolean;
  saveState: SaveState;
  lastEditedBy?: string;
  lastEditedAt?: string | null;
  onChange: (_v: string) => void;
  theme: ReturnType<typeof getTheme>;
  campaignAssets?: CampaignAsset[];
  sessionCharacters?: CharacterListItem[];
  /** data-testid forwarded to the underlying textarea */
  testId?: string;
}

const NotesSection: React.FC<NotesSectionProps> = ({
  label,
  value,
  readOnly = false,
  saveState,
  lastEditedBy,
  lastEditedAt,
  onChange,
  theme,
  campaignAssets = [],
  sessionCharacters = [],
  testId,
}) => {
  const statusColor =
    saveState === "saved"
      ? theme.colors.success
      : saveState === "error"
        ? theme.colors.error
        : theme.colors.textMuted;

  const statusText =
    saveState === "saving"
      ? "Saving…"
      : saveState === "saved"
        ? "Saved ✓"
        : saveState === "error"
          ? "Save failed"
          : "";

  return (
    <div className="flex flex-col h-full">
      {/* Sub-header */}
      <div
        className="flex items-center justify-between px-3 py-1.5 flex-shrink-0 border-b"
        style={{ borderColor: theme.colors.sidebarBorder }}
      >
        <span className="text-xs font-semibold" style={{ color: theme.colors.textSecondary }}>
          {label}
        </span>
        <div className="flex items-center gap-2">
          {lastEditedBy && (
            <span className="text-xs" style={{ color: theme.colors.textMuted }}>
              {lastEditedBy}
              {lastEditedAt
                ? ` · ${new Date(lastEditedAt).toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" })}`
                : ""}
            </span>
          )}
          {statusText ? (
            <span className="text-xs font-medium" style={{ color: statusColor }}>
              {statusText}
            </span>
          ) : null}
        </div>
      </div>

      {/* Markdown editor / preview */}
      <div className="flex-1 min-h-0 overflow-hidden">
        <MarkdownNotes
          value={value}
          onChange={onChange}
          readOnly={readOnly}
          campaignAssets={campaignAssets}
          sessionCharacters={sessionCharacters}
          theme={theme}
          placeholder={readOnly ? "No notes yet." : "Write your notes here…"}
          testId={testId ?? label.replace(/\s+/g, "-").toLowerCase()}
        />
      </div>
    </div>
  );
};

// ─────────────────────────────────────────────────────────────────────────────

interface NotesPanelProps {
  sessionId: string;
  isDM?: boolean;
  campaignId?: number;
  theme?: string;
  className?: string;
}

type ActiveTab = "session" | "personal";

/** Debounce delay before auto-saving (ms) */
const SAVE_DEBOUNCE_MS = 1500;

export const NotesPanel: React.FC<NotesPanelProps> = ({
  sessionId,
  isDM = false,
  campaignId,
  theme = "dark",
  className = "",
}) => {
  const currentTheme = getTheme(theme);
  const { lastMessage } = useWebSocketContext();

  const [activeTab, setActiveTab] = useState<ActiveTab>("session");

  // ── Campaign assets (for [[asset:ID|Name]] references) ───────────────────
  const [campaignAssets, setCampaignAssets] = useState<CampaignAsset[]>([]);

  // ── Session characters (for @[char:ID|Name] mentions) ────────────────────
  const [sessionCharacters, setSessionCharacters] = useState<CharacterListItem[]>([]);

  // ── Shared (collaborative) notes state ──────────────────────────────────
  const [sharedContent, setSharedContent] = useState("");
  const [sharedSaveState, setSharedSaveState] = useState<SaveState>("idle");
  const [sharedLastEditedBy, setSharedLastEditedBy] = useState("");
  const [sharedLastEditedAt, setSharedLastEditedAt] = useState<string | null>(null);

  // ── Personal notes state ─────────────────────────────────────────────────
  const [personalContent, setPersonalContent] = useState("");
  const [personalSaveState, setPersonalSaveState] = useState<SaveState>("idle");

  // ── Lock state ───────────────────────────────────────────────────────────
  const [isLocked, setIsLocked] = useState(false);
  // Ref so the unmount cleanup can read the current lock state without stale closure
  const isLockedRef = useRef(false);

  // Debounce timers
  const sharedTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const personalTimer = useRef<ReturnType<typeof setTimeout> | null>(null);

  // ── Load campaign assets ─────────────────────────────────────────────────

  useEffect(() => {
    if (!campaignId) return;

    CampaignAssetService.getSharedCampaignAssets(campaignId)
      .then(setCampaignAssets)
      .catch((e) => console.error("NotesPanel: failed to load campaign assets", e));
  }, [campaignId]);

  // ── Load session characters ──────────────────────────────────────────────

  useEffect(() => {
    CharacterService.getCharacters()
      .then((chars) => {
        const filtered = campaignId
          ? chars.filter((c) => !c.campaignId || c.campaignId === campaignId)
          : chars;
        setSessionCharacters(filtered);
      })
      .catch((e) => console.error("NotesPanel: failed to load characters", e));
  }, [campaignId]);

  // ── Initial data load ────────────────────────────────────────────────────

  useEffect(() => {
    notesService
      .getSessionNotes(sessionId)
      .then((r) => {
        setSharedContent(r.content);
        setSharedLastEditedBy(r.updated_by);
        setSharedLastEditedAt(r.updated_at);
      })
      .catch((e) => console.error("NotesPanel: failed to load session notes", e));

    // Load initial lock state
    sessionService
      .getSessionDetails(sessionId)
      .then((details) => {
        if (details.are_notes_locked !== undefined) {
          setIsLocked(details.are_notes_locked);
        }
      })
      .catch((e) => console.error("NotesPanel: failed to load session details for lock state", e));

    notesService
      .getPersonalNotes(sessionId)
      .then((r) => setPersonalContent(r.content))
      .catch((e) => console.error("NotesPanel: failed to load personal notes", e));
  }, [sessionId]);

  // ── Real-time sync via WebSocket ─────────────────────────────────────────

  useEffect(() => {
    if (!lastMessage) return;
    if (lastMessage.type !== "notes_updated") return;
    if (String(lastMessage.sessionId) !== String(sessionId)) return;

    const data = lastMessage.data as NotesUpdatedData;
    if (data.isPersonal) return;

    if (!sharedTimer.current) {
      setSharedContent(data.content);
      setSharedLastEditedBy(data.updatedBy);
      setSharedLastEditedAt(data.updatedAt);
    }
  }, [lastMessage, sessionId]);

  useEffect(() => {
    if (!lastMessage) return;
    if (lastMessage.type !== "notes_lock_updated") return;
    if (String(lastMessage.sessionId) !== String(sessionId)) return;

    const data = lastMessage.data as NotesLockUpdatedData;
    isLockedRef.current = data.isLocked;
    setIsLocked(data.isLocked);

    // When notes become locked and the user is not the DM,
    // cancel any pending shared-note save so stale edits don't persist.
    if (data.isLocked && !isDM && sharedTimer.current) {
      clearTimeout(sharedTimer.current);
      sharedTimer.current = null;
    }
  }, [lastMessage, sessionId, isDM]);

  // ── Save helpers (debounced) ─────────────────────────────────────────────

  const saveShared = useCallback(
    (content: string) => {
      setSharedSaveState("saving");
      notesService
        .updateSessionNotes(sessionId, content)
        .then((r) => {
          setSharedSaveState("saved");
          setSharedLastEditedBy(r.updated_by);
          setSharedLastEditedAt(r.updated_at);
          setTimeout(() => setSharedSaveState("idle"), 2000);
        })
        .catch(() => setSharedSaveState("error"));
    },
    [sessionId]
  );

  const savePersonal = useCallback(
    (content: string) => {
      setPersonalSaveState("saving");
      notesService
        .updatePersonalNotes(sessionId, content)
        .then(() => {
          setPersonalSaveState("saved");
          setTimeout(() => setPersonalSaveState("idle"), 2000);
        })
        .catch(() => setPersonalSaveState("error"));
    },
    [sessionId]
  );

  // ── Change handlers ──────────────────────────────────────────────────────

  const handleSharedChange = useCallback(
    (value: string) => {
      setSharedContent(value);
      setSharedSaveState("idle");
      if (sharedTimer.current) clearTimeout(sharedTimer.current);
      sharedTimer.current = setTimeout(() => {
        sharedTimer.current = null;
        saveShared(value);
      }, SAVE_DEBOUNCE_MS);
    },
    [saveShared]
  );

  const handlePersonalChange = useCallback(
    (value: string) => {
      setPersonalContent(value);
      setPersonalSaveState("idle");
      if (personalTimer.current) clearTimeout(personalTimer.current);
      personalTimer.current = setTimeout(() => {
        personalTimer.current = null;
        savePersonal(value);
      }, SAVE_DEBOUNCE_MS);
    },
    [savePersonal]
  );

  // Flush on unmount
  useEffect(() => {
    return () => {
      if (sharedTimer.current) {
        clearTimeout(sharedTimer.current);
        // Only flush shared notes if not locked (or user is DM)
        if (!isLockedRef.current || isDM) {
          saveShared(sharedContent);
        }
      }
      if (personalTimer.current) {
        clearTimeout(personalTimer.current);
        savePersonal(personalContent);
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // ── Render ────────────────────────────────────────────────────────────────

  const tabBase =
    "flex-1 py-1.5 text-xs font-medium transition-colors select-none hover:opacity-90";

  const tabStyle = (tab: ActiveTab) =>
    activeTab === tab
      ? {
          backgroundColor: currentTheme.colors.accent,
          color: "#fff",
        }
      : {
          backgroundColor: currentTheme.colors.surface,
          color: currentTheme.colors.textMuted,
        };

  return (
    <div
      className={`flex flex-col h-full overflow-hidden ${className}`}
      style={{
        backgroundColor: currentTheme.colors.panelBg,
        color: currentTheme.colors.textPrimary,
      }}
      data-testid="notes-panel"
    >
      {/* Panel header */}
      <div
        className="flex items-center gap-2 px-3 py-2 flex-shrink-0 border-b"
        style={{
          backgroundColor: currentTheme.colors.panelHeader,
          borderColor: currentTheme.colors.sidebarBorder,
        }}
      >
        <span className="text-base" aria-hidden="true">
          📝
        </span>
        <span className="text-sm font-semibold">Notes</span>
        {isLocked && (
          <span
            className="ml-auto text-[10px] font-bold uppercase tracking-wider flex items-center gap-1 px-1.5 py-0.5 rounded bg-amber-500/20 text-amber-500 border border-amber-500/30"
            title={isDM ? "Locked for players" : "Locked by Dungeon Master"}
          >
            🔒 Locked
          </span>
        )}
      </div>

      {/* Tab switcher */}
      <div
        className="flex flex-shrink-0 border-b"
        style={{ borderColor: currentTheme.colors.sidebarBorder }}
        role="tablist"
      >
        <button
          role="tab"
          aria-selected={activeTab === "session"}
          aria-controls="notes-session-tab"
          onClick={() => setActiveTab("session")}
          className={tabBase}
          style={tabStyle("session")}
          data-testid="notes-tab-session"
        >
          🤝 Party Log
        </button>
        <button
          role="tab"
          aria-selected={activeTab === "personal"}
          aria-controls="notes-personal-tab"
          onClick={() => setActiveTab("personal")}
          className={tabBase}
          style={tabStyle("personal")}
          data-testid="notes-tab-personal"
        >
          📓 Field Notes
        </button>
      </div>

      {/* Tab content */}
      <div className="flex-1 min-h-0 overflow-hidden">
        {activeTab === "session" ? (
          <div id="notes-session-tab" role="tabpanel" className="h-full flex flex-col">
            <NotesSection
              label="Visible to all companions"
              value={sharedContent}
              readOnly={isLocked && !isDM}
              saveState={sharedSaveState}
              lastEditedBy={sharedLastEditedBy ? `Last edit: ${sharedLastEditedBy}` : ""}
              lastEditedAt={sharedLastEditedAt}
              onChange={handleSharedChange}
              theme={currentTheme}
              campaignAssets={campaignAssets}
              sessionCharacters={sessionCharacters}
              testId="notes-textarea-shared-with-everyone-in-the-session"
            />
          </div>
        ) : (
          <div id="notes-personal-tab" role="tabpanel" className="h-full flex flex-col">
            <NotesSection
              label="Private — only you can see these"
              value={personalContent}
              saveState={personalSaveState}
              onChange={handlePersonalChange}
              theme={currentTheme}
              campaignAssets={campaignAssets}
              sessionCharacters={sessionCharacters}
              testId="notes-textarea-private-—-only-you-can-see-these"
            />
          </div>
        )}
      </div>
    </div>
  );
};
